// StaleDataBanner — a PROMINENT, board-honest warning shown ONLY when a levy
// data source is stale or its last pull failed. The subtle per-source chips
// (FreshnessStrip) are always-on; this banner is the loud "don't trust these
// numbers as current" signal so the Board never reads a stale figure as today's
// position. Renders nothing when everything is fresh.
//
// Data: GET /api/finance/freshness (useFinanceFreshness). Threshold + status are
// computed server-side (finance-freshness.ts) so the UI, the daily alert, and
// the weekly-email guard all agree on one definition of "stale".

import { AlertTriangle } from "lucide-react";
import { useFinanceFreshness } from "../../hooks/useFinance";

// Render an "as of" value: date-only strings as a calendar date (no tz shift);
// full timestamps converted to Eastern.
function fmtAsOf(asOf: string | null): string {
  if (!asOf) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
    const [y, m, d] = asOf.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(asOf);
  const iso =
    (asOf.includes("T") ? asOf : asOf.replace(" ", "T")) + (hasZone ? "" : "Z");
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return asOf;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function StaleDataBanner({
  className = "",
}: {
  className?: string;
}) {
  const { data } = useFinanceFreshness();
  if (!data || !data.any_stale) return null;
  const stale = data.sources.filter(
    (s) => s.status === "stale" || s.status === "failed",
  );
  if (stale.length === 0) return null;

  const anyFailed = stale.some((s) => s.status === "failed");
  const tone = anyFailed
    ? "bg-status-red/12 border-status-red/40 text-status-red"
    : "bg-status-amber/12 border-status-amber/40 text-status-amber";

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${tone} ${className}`}
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="text-sm">
        <p className="font-semibold">
          Some levy data isn’t current — figures below may not reflect today’s
          position.
        </p>
        <p className="mt-1 text-theme-muted">
          {stale.map((s, i) => (
            <span key={s.source}>
              {i > 0 ? " · " : ""}
              <span className="font-medium">{s.label}</span>:{" "}
              {s.status === "failed"
                ? "last pull failed"
                : `${s.age_days ?? "?"} day${s.age_days === 1 ? "" : "s"} old`}
              {s.as_of ? ` (as of ${fmtAsOf(s.as_of)})` : ""}
            </span>
          ))}
        </p>
        {stale.find((s) => s.note)?.note ? (
          <p className="mt-1 text-xs text-theme-muted">
            {stale.find((s) => s.note)?.note}
          </p>
        ) : null}
      </div>
    </div>
  );
}
